import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getCached, setCached } from '../lib/hookCache'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { usePhoenixRefresh } from './usePhoenixRefresh'
import { useQueueByTable } from './useQueueByTable'
import { enqueueMutation } from '../lib/mutationQueue'

const TABLE = 'phases'
const CACHE_SCOPE = 'phases'

// Lists all phases (newest first by start_date). Writes flow through the
// mutation queue so offline edits stay durable.
export function usePhases() {
  const { user } = useAuth()
  const cached = getCached(CACHE_SCOPE, user?.id)
  const [serverPhases, setServerPhases] = useState(cached || [])
  const [loading, setLoading] = useState(!cached)
  const [error, setError] = useState(null)
  const pending = useQueueByTable(TABLE)
  const initialLoadedRef = useRef(!!cached)

  const fetchPhases = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from(TABLE)
      .select('*')
      .order('start_date', { ascending: false })
    if (err) {
      setError(err.message)
    } else {
      const next = data || []
      setServerPhases(next)
      setCached(CACHE_SCOPE, user.id, next)
    }
    initialLoadedRef.current = true
    setLoading(false)
  }, [user])

  useEffect(() => { fetchPhases() }, [fetchPhases])
  usePhoenixRefresh(fetchPhases)

  // Optimistic merge of queued mutations onto the server snapshot.
  const phases = useMemo(() => {
    let view = [...serverPhases]
    for (const m of pending) {
      if (m.op === 'upsert') {
        const idx = view.findIndex((p) => p.id === m.payload.id)
        if (idx >= 0) view[idx] = { ...view[idx], ...m.payload, _pending: m }
        else view.unshift({ ...m.payload, _pending: m })
      } else if (m.op === 'update') {
        const idx = view.findIndex((p) => p.id === m.payload.id)
        if (idx >= 0) view[idx] = { ...view[idx], ...m.payload, _pending: m }
      } else if (m.op === 'delete') {
        view = view.filter((p) => p.id !== m.payload.id)
      }
    }
    view.sort((a, b) => (a.start_date < b.start_date ? 1 : a.start_date > b.start_date ? -1 : 0))
    return view
  }, [serverPhases, pending])

  // Find the existing current phase (start <= today, end >= today or null).
  // Used by createPhase to auto-close it when a new one starts overlapping.
  const findCurrentPhase = (list) => {
    const today = new Date().toLocaleDateString('en-CA')
    return list.find((p) => {
      if (p.start_date > today) return false
      if (!p.end_date) return true
      return p.end_date >= today
    }) || null
  }

  const createPhase = async (values) => {
    if (!user) return false
    setError(null)
    const id = crypto.randomUUID()
    const newStart = values.start_date

    // Auto-close any current phase that overlaps the new one.
    const overlapping = findCurrentPhase(phases)
    if (overlapping && (!overlapping.end_date || overlapping.end_date >= newStart)) {
      const closeDate = new Date(newStart + 'T00:00:00')
      closeDate.setDate(closeDate.getDate() - 1)
      const closeStr = closeDate.toLocaleDateString('en-CA')
      await enqueueMutation({
        id: overlapping.id,
        table: TABLE,
        op: 'update',
        payload: { id: overlapping.id, end_date: closeStr, updated_at: new Date().toISOString() },
        userId: user.id,
      })
    }

    await enqueueMutation({
      id,
      table: TABLE,
      op: 'upsert',
      payload: {
        id,
        user_id: user.id,
        name: values.name?.trim() || null,
        phase_type: values.phase_type,
        start_date: values.start_date,
        end_date: values.end_date || null,
        start_weight_kg: values.start_weight_kg,
        goal_weight_kg: values.goal_weight_kg ?? null,
        notes: values.notes?.trim() || null,
        updated_at: new Date().toISOString(),
      },
      conflictKey: 'id',
      userId: user.id,
    })
    return true
  }

  const updatePhase = async (id, values) => {
    if (!user) return false
    setError(null)
    const payload = { id, updated_at: new Date().toISOString() }
    if (values.name !== undefined) payload.name = values.name?.trim() || null
    if (values.phase_type !== undefined) payload.phase_type = values.phase_type
    if (values.start_date !== undefined) payload.start_date = values.start_date
    if (values.end_date !== undefined) payload.end_date = values.end_date || null
    if (values.start_weight_kg !== undefined) payload.start_weight_kg = values.start_weight_kg
    if (values.goal_weight_kg !== undefined) payload.goal_weight_kg = values.goal_weight_kg ?? null
    if (values.notes !== undefined) payload.notes = values.notes?.trim() || null

    await enqueueMutation({
      id,
      table: TABLE,
      op: 'update',
      payload,
      userId: user.id,
    })
    return true
  }

  const deletePhase = async (id) => {
    if (!user) return false
    setError(null)
    await enqueueMutation({
      id,
      table: TABLE,
      op: 'delete',
      payload: { id },
      userId: user.id,
    })
    return true
  }

  return {
    phases,
    loading,
    error,
    createPhase,
    updatePhase,
    deletePhase,
    refetch: fetchPhases,
  }
}
